/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router'
import { useServiceContext } from '../../../root'
import SiteInfo from '../../../components/staging/SiteInfo'
import useDocUploadDataService from '../../../hooks/useDocUploadDataService'

const StagingArea: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const context = useServiceContext()
  const { service } = context
  const { translate } = service.filter
  const [level, setLevel] = useState<string | null>(null)
  const [contextId, setContextId] = useState<string | null>(null)
  const { assembleDocUploadData } = useDocUploadDataService()

  useEffect(() => {
    // Get the siteId from query params
    const siteId = searchParams.get('siteId')
    const environmentId = searchParams.get('environmentId')

    // Set context from URL params if not already set (belt-and-suspenders —
    // SitesTable.tsx calls setSiteId before navigate, but catch page-reload cases too)
    if (siteId) service.staging.setSiteId(siteId)
    if (environmentId) service.staging.setEnvironmentId(environmentId)

    // Get level from staging service
    const stagingLevel = service.staging.getLevel()
    setLevel(stagingLevel || null)

    // Get context ID from staging service
    const stagingContextId = service.staging.getContextId()
    setContextId(stagingContextId ? String(stagingContextId) : null)

    // Explicitly emit the checkmate action for the staging area.
    // Legacy equivalent: $stateChangeSuccess for app.staging.stagingArea → stagingService.emitCheckmateAction().
    // root.tsx also calls this via onStateChange('staging'), but that fires at the /staging/*
    // level and may be in-flight. Calling here directly on mount ensures the header always
    // updates when the user lands on the staging area (e.g. clicking a site from the overview table).
    service.staging.emitCheckmateAction().catch((err) => {
      console.error('[StagingArea] Error emitting checkmate action:', err)
    })
  }, [searchParams, service.staging])

  const handleMissingDocsClick = () => {
    navigate('/staging/MissingDocuments')
  }

  const handleUploadDocsClick = () => {
    assembleDocUploadData()
  }

  const handleUserDocsClick = () => {
    navigate('/staging/MyDocuments')
  }

  const handleSiteDocsClick = () => {
    navigate('/staging/Documents')
  }

  const handleStudyDocsClick = () => {
    navigate('/staging/StudyDocuments')
  }

  return (
    <div className="mcc-content-frame theme-sandman">
      <h1>{translate('STAGING_AREA_SECTION')}</h1>
      <div id="site-docs-staging-area" className="d-flex flex-column staging-area">
        <div className="align-self-start align-top align-left ng-scope">
          <div className="pull-left">{contextId && level && <SiteInfo contextId={contextId} level={level} />}</div>
        </div>
        <div id="page-content" className="d-flex flex-grow align-self-stretch justify-content-center">
          <div className="action-buttons">
            <div className="row">
              <div className="col-xs-6 d-flex">
                <a className="docs-missing cursor-pointer" onClick={handleMissingDocsClick}>
                  {translate('STAGING_AREA_SECTION_MISSING_DOCS')}
                </a>
              </div>
              <div className="col-xs-6 d-flex">
                <a className="doc-upload cursor-pointer" onClick={handleUploadDocsClick}>
                  {translate('STAGING_AREA_SECTION_UPLOAD_DOCS')}
                </a>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-6 d-flex">
                <a className="docs-user cursor-pointer" onClick={handleUserDocsClick}>
                  {translate('STAGING_AREA_SECTION_USER_DOCS')}
                </a>
              </div>
              <div className="col-xs-6 d-flex">
                {level === 'site' && (
                  <a className="docs-site cursor-pointer" onClick={handleSiteDocsClick}>
                    {translate('STAGING_AREA_SECTION_SITE_DOCS')}
                  </a>
                )}
                {level === 'environment' && (
                  <a className="docs-study cursor-pointer" onClick={handleStudyDocsClick}>
                    {translate('STAGING_AREA_SECTION_STUDY_DOCS')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StagingArea
